import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { Mobilization, MobilizationAuditEvent } from '../models/mobilization.model';
import { MobilizationService } from '../mobilization.service';
import { RentalAssignmentCostPreview } from '../../rental/rental.model';
import { RentalService } from '../../rental/rental.service';

@Component({
    selector: 'app-mobilization-detail',
    standalone: true,
    templateUrl: './mobilization-detail.component.html',
    styleUrls: ['./mobilization-detail.component.css'],
    imports: [
        CommonModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        TagModule,
        TimelineModule,
        ToastModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class MobilizationDetailComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    mobilization?: Mobilization;
    rentalCostPreview?: RentalAssignmentCostPreview;
    isLoading = false;
    isActing = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private mobilizationService: MobilizationService,
        private rentalService: RentalService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Mobilization', routerLink: '/fleet/mobilization' }, { label: 'Detail' }];
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (id) {
            this.loadMobilization(id);
        }
    }

    loadMobilization(id = this.mobilization?.mobilizationId): void {
        if (!id) {
            return;
        }
        this.isLoading = true;
        this.mobilizationService.getMobilization(id).subscribe({
            next: response => {
                this.mobilization = response.data;
                this.loadRentalCostPreview();
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load mobilization.' });
            }
        });
    }

    submit(): void {
        this.runAction('submit', 'Submitted for approval.');
    }

    approve(): void {
        this.runAction('approve', 'Approved.');
    }

    reject(): void {
        this.runAction('reject', 'Rejected from detail page.');
    }

    activate(): void {
        this.runAction('activate', 'Activated for site work.');
    }

    complete(): void {
        this.runAction('complete', 'Completed.');
    }

    cancel(): void {
        this.runAction('cancel', 'Cancelled from detail page.');
    }

    recalculateSalary(): void {
        const id = this.mobilization?.mobilizationId;
        if (!id) {
            return;
        }
        this.isActing = true;
        this.mobilizationService.recalculateSalary(id).subscribe({
            next: response => {
                this.mobilization = response.data;
                this.isActing = false;
                this.messageService.add({ severity: 'success', summary: 'Salary updated', detail: 'Mobilization salary was recalculated.' });
                this.loadMobilization(id);
            },
            error: error => {
                this.isActing = false;
                this.messageService.add({ severity: 'error', summary: 'Recalculate failed', detail: error?.error?.message || 'Could not recalculate salary.' });
            }
        });
    }

    edit(): void {
        if (this.mobilization?.mobilizationId) {
            this.router.navigate(['/fleet/mobilization/edit', this.mobilization.mobilizationId]);
        }
    }

    openLogisticsProject(): void {
        if (this.mobilization?.logisticsProjectId) {
            this.router.navigate(['/logistics-projects', this.mobilization.logisticsProjectId]);
        }
    }

    metrics(): PageMetric[] {
        const days = this.assignmentDays();
        return [
            { label: 'Status', value: this.mobilization?.status ?? '-', hint: 'Current lifecycle state', tone: this.statusTone() },
            { label: 'Duration', value: days ?? '-', hint: days ? 'Planned calendar days' : 'Date range missing', tone: 'info' },
            { label: 'Trailer', value: this.mobilization?.trailerAssetNumber || 'None', hint: this.mobilization?.trailerTypeName || 'Optional asset', tone: this.mobilization?.trailerId ? 'success' : 'default' },
            { label: 'Salary', value: this.salaryLabel(), hint: this.mobilization?.salaryCalculationStatus || 'Not calculated', tone: this.mobilization?.salaryCalculationStatus === 'NO_RULE' ? 'danger' : 'info' },
            { label: 'Audit Events', value: this.mobilization?.auditEvents?.length ?? 0, hint: 'Recorded lifecycle actions', tone: 'default' }
        ];
    }

    projectLabel(): string {
        const item = this.mobilization;
        if (!item) {
            return '-';
        }
        return item.projectSource === 'LOGISTICS_PROJECT'
            ? (item.logisticsProjectId ? `Logistics Project #${item.logisticsProjectId}` : 'Logistics Project')
            : [item.manualProjectCode, item.manualProjectName].filter(Boolean).join(' - ') || 'Manual project';
    }

    siteLabel(): string {
        const item = this.mobilization;
        if (!item) {
            return '-';
        }
        return item.manualSiteName || item.manualSiteAddress || 'Site not set';
    }

    projectSourceLabel(source?: string | null): string {
        switch (source) {
            case 'LOGISTICS_PROJECT':
                return 'Logistics Project';
            case 'MANUAL':
                return 'Manual';
            default:
                return source || 'Project';
        }
    }

    trailerLabel(): string {
        return this.mobilization?.trailerAssetNumber || this.mobilization?.trailerPlateNumber || 'No trailer';
    }

    supervisorLabel(): string {
        if (!this.mobilization?.supervisorName && !this.mobilization?.supervisorEmployeeId) {
            return 'Supervisor not set';
        }
        return [
            this.mobilization?.supervisorName,
            this.mobilization?.supervisorEmployeeId ? `#${this.mobilization.supervisorEmployeeId}` : ''
        ].filter(Boolean).join(' ');
    }

    salaryLabel(): string {
        if (this.mobilization?.salaryCalculationStatus === 'NO_RULE') {
            return 'No rule';
        }
        if (this.mobilization?.salaryTotalAmount === null || this.mobilization?.salaryTotalAmount === undefined) {
            return '-';
        }
        return `${this.mobilization.salaryTotalAmount} ${this.mobilization.salaryCurrencyCode || 'SAR'}`;
    }

    rentalCostLabel(): string {
        if (!this.rentalCostPreview?.lines?.some(line => line.status === 'ELIGIBLE')) return 'No daily rental cost';
        return `${this.rentalCostPreview.estimatedCost ?? 0} ${this.rentalCostPreview.currencyCode || ''}`.trim();
    }

    private loadRentalCostPreview(): void {
        const item = this.mobilization;
        if (!item?.owningCompanyId || !item.startDate || !item.endDate) { this.rentalCostPreview = undefined; return; }
        this.rentalService.previewAssignmentCost(item.owningCompanyId, item.vehicleId, item.driverId,
            formatDate(item.startDate, 'yyyy-MM-dd', 'en-US'), formatDate(item.endDate, 'yyyy-MM-dd', 'en-US')).subscribe({
                next: response => this.rentalCostPreview = response.data,
                error: () => this.rentalCostPreview = undefined
            });
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    dateTimeLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y HH:mm', 'en-US') : '-';
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (status) {
            case 'ACTIVE':
            case 'COMPLETED':
                return 'success';
            case 'SUBMITTED':
            case 'APPROVED':
                return 'warning';
            case 'REJECTED':
            case 'CANCELLED':
                return 'danger';
            case 'DRAFT':
                return 'secondary';
            default:
                return 'info';
        }
    }

    auditSeverity(event: MobilizationAuditEvent): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        if (event.actionCode === 'SUPERVISOR_CHANGE') {
            return 'info';
        }
        return this.statusSeverity(event.toStatus);
    }

    actionLabel(actionCode?: string): string {
        return (actionCode || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
    }

    canSubmit(): boolean {
        return this.mobilization?.status === 'DRAFT' || this.mobilization?.status === 'REJECTED';
    }

    canApproveReject(): boolean {
        return this.mobilization?.status === 'SUBMITTED';
    }

    canActivate(): boolean {
        return this.mobilization?.status === 'APPROVED';
    }

    canComplete(): boolean {
        return this.mobilization?.status === 'ACTIVE';
    }

    canCancel(): boolean {
        return !!this.mobilization?.status && ['DRAFT', 'SUBMITTED', 'APPROVED', 'ACTIVE'].includes(this.mobilization.status);
    }

    private runAction(action: 'submit' | 'approve' | 'reject' | 'activate' | 'complete' | 'cancel', comment: string): void {
        const id = this.mobilization?.mobilizationId;
        if (!id) {
            return;
        }
        this.isActing = true;
        this.mobilizationService[action](id, this.actorAction(comment)).subscribe({
            next: response => {
                this.mobilization = response.data;
                this.isActing = false;
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Mobilization ${action} action completed.` });
                this.loadMobilization(id);
            },
            error: error => {
                this.isActing = false;
                this.messageService.add({ severity: 'error', summary: 'Action failed', detail: error?.error?.message || `Could not ${action} mobilization.` });
            }
        });
    }

    private assignmentDays(): number | null {
        if (!this.mobilization?.startDate || !this.mobilization?.endDate) {
            return null;
        }
        const start = new Date(this.mobilization.startDate).getTime();
        const end = new Date(this.mobilization.endDate).getTime();
        return Math.max(1, Math.round((end - start) / 86400000) + 1);
    }

    private statusTone(): PageMetric['tone'] {
        switch (this.mobilization?.status) {
            case 'ACTIVE':
            case 'COMPLETED':
                return 'success';
            case 'SUBMITTED':
            case 'APPROVED':
                return 'warn';
            case 'REJECTED':
            case 'CANCELLED':
                return 'danger';
            default:
                return 'default';
        }
    }

    private actorAction(comment: string) {
        const user = this.sessionStorageService.getCustomer();
        return {
            actorCustomerId: user?.id,
            actorName: user?.name || 'Current user',
            comment
        };
    }
}
